import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface ToastOptions {
  /** Duração em ms. `0` = persistente até o usuário fechar. Padrão 5000. */
  duration?: number;
  /** Título opcional (negrito acima da mensagem). */
  title?: string;
  /** Slot para uma ação (ex.: botão "Desfazer"). */
  action?: React.ReactNode;
}

interface ToastItem extends ToastOptions {
  id: number;
  variant: ToastVariant;
  message: string;
}

interface ToastContextValue {
  success: (message: string, options?: ToastOptions) => number;
  error: (message: string, options?: ToastOptions) => number;
  info: (message: string, options?: ToastOptions) => number;
  warning: (message: string, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_STYLE: Record<ToastVariant, { box: string; icon: React.ReactNode; iconColor: string }> = {
  info: {
    box: 'border-blue-200 bg-blue-50 text-blue-900',
    icon: <Info className="h-5 w-5" aria-hidden />,
    iconColor: 'text-blue-600',
  },
  success: {
    box: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    icon: <CheckCircle2 className="h-5 w-5" aria-hidden />,
    iconColor: 'text-emerald-600',
  },
  warning: {
    box: 'border-amber-200 bg-amber-50 text-amber-900',
    icon: <AlertTriangle className="h-5 w-5" aria-hidden />,
    iconColor: 'text-amber-600',
  },
  error: {
    box: 'border-rose-200 bg-rose-50 text-rose-900',
    icon: <XCircle className="h-5 w-5" aria-hidden />,
    iconColor: 'text-rose-600',
  },
};

const DEFAULT_DURATION_MS = 5000;

/**
 * Sistema de toasts efêmeros para feedback de operações.
 *
 * Diferente do `<Alert>` (callout inline persistente na página), o toast
 * flutua no canto superior direito e auto-descarta em 5s por padrão.
 * Reusa a mesma paleta e ícones do Alert pra manter consistência visual.
 *
 * Uso:
 *   const toast = useToast();
 *   toast.success('Registro salvo');
 *   toast.error('Falha', { title: 'Erro', duration: 8000 });
 *   toast.info('Processando…', { duration: 0 }); // persistente
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const enqueue = useCallback(
    (variant: ToastVariant, message: string, options?: ToastOptions): number => {
      const id = nextId.current++;
      setToasts((current) => [
        ...current,
        { id, variant, message, duration: options?.duration, title: options?.title, action: options?.action },
      ]);
      return id;
    },
    [],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message, options) => enqueue('success', message, options),
      error: (message, options) => enqueue('error', message, options),
      info: (message, options) => enqueue('info', message, options),
      warning: (message, options) => enqueue('warning', message, options),
      dismiss,
    }),
    [enqueue, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pointer-events-none fixed top-4 right-4 z-[9999] flex w-full max-w-sm flex-col gap-2"
            role="region"
            aria-label="Notificações"
          >
            {toasts.map((t) => (
              <ToastCard key={t.id} toast={t} onClose={() => dismiss(t.id)} />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast deve ser usado dentro de <ToastProvider>');
  }
  return ctx;
}

interface ToastCardProps {
  toast: ToastItem;
  onClose: () => void;
}

function ToastCard({ toast, onClose }: ToastCardProps) {
  const cfg = VARIANT_STYLE[toast.variant];
  const duration = toast.duration ?? DEFAULT_DURATION_MS;
  const [visible, setVisible] = useState(false);

  // Fade+slide de entrada: monta invisível/deslocado, e no tick seguinte
  // vira visível para a transição CSS acontecer.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (duration <= 0) return;
    const timer = window.setTimeout(onClose, duration);
    return () => window.clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div
      role={toast.variant === 'error' || toast.variant === 'warning' ? 'alert' : 'status'}
      className={`pointer-events-auto flex items-start gap-3 rounded-md border p-3 text-sm shadow-lg transition-all duration-200 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      } ${cfg.box}`}
    >
      <span className={`mt-0.5 shrink-0 ${cfg.iconColor}`}>{cfg.icon}</span>
      <div className="flex-1 min-w-0">
        {toast.title && <p className="font-medium">{toast.title}</p>}
        <div className={toast.title ? 'mt-1' : ''}>{toast.message}</div>
        {toast.action && <div className="mt-2">{toast.action}</div>}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 rounded p-0.5 text-gray-500 hover:bg-black/5 hover:text-gray-700"
        aria-label="Fechar notificação"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
